import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { BookOpen, LogOut, Sparkles, Camera, Calendar as CalendarIcon } from 'lucide-react';
import { getUserRecipes } from '../utils/recipeService';
import BottomNav from '../components/BottomNav';

export default function Home() {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [recipeCount, setRecipeCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, [currentUser]);

  async function loadStats() {
    try {
      const recipes = await getUserRecipes(currentUser.uid);
      setRecipeCount(recipes.length);
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleLogout() {
    try {
      await logout();
      navigate('/login');
    } catch (err) {
      console.error('Failed to log out', err);
    }
  }

  return (
    <div className="min-h-screen pb-24" style={{ background: 'linear-gradient(135deg, #f8f0f5 0%, #e8e4f3 100%)' }}>
      {/* Flowing Blob Header */}
      <div className="relative h-80 overflow-hidden mb-6">
        {/* Purple Blob */}
        <div 
          className="absolute w-96 h-96 rounded-full opacity-90"
          style={{
            background: 'linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)',
            top: '-80px',
            left: '-80px',
            borderRadius: '40% 60% 70% 30% / 40% 50% 60% 50%',
            animation: 'morph 8s ease-in-out infinite',
          }}
        />
        
        {/* Pink Blob */}
        <div 
          className="absolute w-80 h-80 rounded-full opacity-90"
          style={{
            background: 'linear-gradient(135deg, #fb7185 0%, #fda4af 100%)',
            top: '80px',
            right: '-100px',
            borderRadius: '60% 40% 30% 70% / 50% 60% 40% 50%',
            animation: 'morph 10s ease-in-out infinite reverse',
          }}
        />
        
        {/* Yellow Blob */}
        <div 
          className="absolute w-64 h-64 rounded-full"
          style={{
            background: 'linear-gradient(135deg, #ffe082 0%, #fff59d 100%)',
            bottom: '-60px',
            left: '100px',
            opacity: 0.85,
            borderRadius: '40% 60% 70% 30% / 40% 50% 60% 50%',
            animation: 'morph 12s ease-in-out infinite',
          }}
        />
        
        {/* Header Content */}
        <div className="relative z-10 max-w-md mx-auto px-6 pt-16">
          <div className="flex items-center justify-between mb-2">
            <h1 
              className="text-white text-5xl logo"
              style={{
                textShadow: '0 2px 20px rgba(0,0,0,0.3), 0 4px 8px rgba(0,0,0,0.2)',
              }}
            >
              AllThese
            </h1>
            <button
              onClick={handleLogout}
              className="bg-white/20 backdrop-blur-sm p-2.5 rounded-xl hover:bg-white/30 transition-colors"
              style={{
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              }}
            >
              <LogOut size={20} className="text-white" />
            </button>
          </div>
          <p 
            className="text-white/95 text-base"
            style={{
              textShadow: '0 2px 10px rgba(0,0,0,0.2)',
            }}
          >
            Your family meal planner ✨
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-md mx-auto px-6 -mt-16 relative z-20">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div 
            className="p-7 text-center rounded-3xl"
            style={{
              background: 'linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)',
              boxShadow: '0 10px 30px rgba(124, 58, 237, 0.2)',
            }}
          >
            <div 
              className="text-5xl font-extrabold text-white mb-1"
              style={{
                textShadow: '0 2px 10px rgba(0,0,0,0.1)',
              }}
            >
              {loading ? '-' : recipeCount}
            </div>
            <div className="text-sm text-white/95 font-medium">Recipes Saved</div>
          </div>
          
          <div 
            className="p-7 text-center rounded-3xl"
            style={{
              background: 'linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)',
              boxShadow: '0 10px 30px rgba(124, 58, 237, 0.2)',
            }}
          >
            <div 
              className="text-5xl font-extrabold text-white mb-1"
              style={{
                textShadow: '0 2px 10px rgba(0,0,0,0.1)',
              }}
            >
              {loading ? '-' : recipeCount}
            </div>
            <div className="text-sm text-white/95 font-medium">This Week</div>
          </div>
        </div>

        {/* Add Recipe Button */}
        <button
          onClick={() => navigate('/upload')}
          className="btn-primary w-full mb-5"
        >
          <Sparkles size={20} />
          Add Recipe from Photo
        </button>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <button
            onClick={() => navigate('/library')}
            className="bg-white p-6 rounded-3xl font-semibold flex flex-col items-center gap-3 border-2 border-gray-100 hover:border-purple-500 transition-all shadow-md hover:shadow-lg hover:-translate-y-1"
          >
            <BookOpen size={40} className="text-purple-600" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }} />
            <span className="text-sm text-gray-700">My Recipes</span>
          </button>
          
          <button
            onClick={() => navigate('/calendar')}
            className="bg-white p-6 rounded-3xl font-semibold flex flex-col items-center gap-3 border-2 border-gray-100 hover:border-purple-500 transition-all shadow-md hover:shadow-lg hover:-translate-y-1"
          >
            <CalendarIcon size={40} className="text-purple-600" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }} />
            <span className="text-sm text-gray-700">Meal Plan</span>
          </button>
        </div>

        {/* Recent Recipes Section */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4 px-1">
            <h3 className="text-xl font-bold text-gray-800">Recent Recipes</h3>
            <button 
              onClick={() => navigate('/library')}
              className="text-purple-600 text-sm font-semibold hover:underline"
            >
              See all →
            </button>
          </div>

          {recipeCount === 0 ? (
            <div className="bg-white rounded-3xl p-8 text-center shadow-md border border-gray-100">
              <div className="text-6xl mb-4">📸</div>
              <h4 className="font-bold text-gray-800 mb-2">No recipes yet!</h4>
              <p className="text-sm text-gray-600 mb-4">
                Start by uploading a recipe screenshot
              </p>
              <button
                onClick={() => navigate('/upload')}
                className="text-purple-600 font-semibold text-sm hover:underline"
              >
                Upload your first recipe →
              </button>
            </div>
          ) : (
            <div className="bg-white rounded-3xl overflow-hidden shadow-md border border-gray-100 hover:shadow-lg transition-all hover:-translate-y-1">
              <div 
                className="h-36 flex items-center justify-center text-6xl"
                style={{ background: 'linear-gradient(135deg, #e0e7ff 0%, #fce7f3 100%)' }}
              >
                🌮
              </div>
              <div className="p-5">
                <h4 className="font-bold text-gray-800 mb-2">Sample Recipe</h4>
                <p className="text-sm text-gray-600 mb-3">8 ingredients • Budget Baddies</p>
                <div className="flex gap-2">
                  <span className="tag-pink">Quick</span>
                  <span className="tag-pink">Family Fave</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      <BottomNav />
    </div>
  );
}
